import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

const DURATION_LABELS = { WEEK: 'Woche', MONTH: 'Monat', YEAR: 'Jahr', UNLIMITED: 'Unbegrenzt' };

const PRODUCT_TEMPLATES = {
  monthly: {
    name: 'Monatlich',
    description: 'Monatsabo mit voller Funktionalität.',
    short_description: 'Für alle, die flexibel monatlich starten möchten.',
    image_url: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=900&q=80',
    image_alt: 'Monatslizenz für digitale Produkte',
    price: '9.99',
    currency: 'EUR',
    duration_type: 'MONTH',
    is_active: true,
    sort_order: 1,
    highlight: false,
    badge: 'Beliebt',
    features: 'Vollzugriff\nSofort aktiv\nWöchentliche Updates',
    plan_key: 'monthly',
    modules: 'core,updates',
    user_limit: '',
  },
  yearly: {
    name: 'Jährlich',
    description: 'Jahresabo mit günstigem Gesamtpreis.',
    short_description: 'Beste Wahl für langfristige Nutzung mit Rabatt.',
    image_url: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=900&q=80',
    image_alt: 'Jahreslizenz für langfristigen Zugriff',
    price: '89.99',
    currency: 'EUR',
    duration_type: 'YEAR',
    is_active: true,
    sort_order: 2,
    highlight: true,
    badge: 'Spart 2 Monate',
    features: 'Vollzugriff\nJährliche Nutzung\nPriorisierter Support',
    plan_key: 'yearly',
    modules: 'core,support,updates',
    user_limit: '',
  },
  lifetime: {
    name: 'Lifetime',
    description: 'Einmalige Zahlung für dauerhaften Zugriff.',
    short_description: 'Für Kunden mit Langfristperspektive ohne Abo.',
    image_url: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=900&q=80',
    image_alt: 'Lifetime-Lizenz mit dauerhaftem Zugriff',
    price: '199.00',
    currency: 'EUR',
    duration_type: 'UNLIMITED',
    is_active: true,
    sort_order: 3,
    highlight: true,
    badge: 'Lifetime',
    features: 'Dauerhafter Zugriff\nKein Ablauf\nIndividuelle Betreuung',
    plan_key: 'lifetime',
    modules: 'core,priority',
    user_limit: '',
  },
};

const emptyProductForm = {
  name: '', description: '', short_description: '', image_url: '', image_alt: '', price: '', currency: 'EUR',
  duration_type: 'MONTH', is_active: true, sort_order: 0,
  highlight: false, badge: '', features: '',
  plan_key: '', modules: '', user_limit: '',
  product_key: '', product_type: 'LICENSED', customer_limit: '',
};

// Leere Eingaben als null senden (product_key ist eindeutig, '' würde kollidieren)
const productPayload = (form) => ({
  ...form,
  price: String(form.price),
  sort_order: parseInt(form.sort_order) || 0,
  user_limit: form.user_limit === '' ? null : parseInt(form.user_limit),
  customer_limit: form.customer_limit === '' ? null : parseInt(form.customer_limit),
  product_key: form.product_key ? form.product_key.trim() : null,
});

function ProductManagement() {
  const [programs, setPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState('');
  const [groups, setGroups] = useState([]);
  const [expanded, setExpanded] = useState(new Set());
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [discordRoleId, setDiscordRoleId] = useState('');
  const [discordRoleSaving, setDiscordRoleSaving] = useState(false);
  const [discordRoleMsg, setDiscordRoleMsg] = useState('');

  // Group form state
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [groupForm, setGroupForm] = useState({ name: '', description: '', sort_order: 0 });
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editGroupForm, setEditGroupForm] = useState({ name: '', description: '', sort_order: 0 });

  // Product form state
  const [addingInGroup, setAddingInGroup] = useState(null);
  const [productForm, setProductForm] = useState(emptyProductForm);
  const [editingProductId, setEditingProductId] = useState(null);
  const [editProductForm, setEditProductForm] = useState(emptyProductForm);

  const fetchPrograms = useCallback(async () => {
    try {
      const r = await api.get('/programs/');
      setPrograms(r.data);
    } catch { setError('Fehler beim Laden der Programme.'); }
  }, []);

  const fetchGroups = useCallback(async (programId) => {
    if (!programId) { setGroups([]); return; }
    setLoading(true);
    setError('');
    try {
      const r = await api.get(`/products/groups?program_id=${programId}`);
      setGroups(r.data);
    } catch { setError('Fehler beim Laden der Produktgruppen.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPrograms(); }, [fetchPrograms]);
  useEffect(() => { fetchGroups(selectedProgramId); }, [fetchGroups, selectedProgramId]);

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ── Group operations ──────────────────────────────────────────────────────

  const handleAddGroup = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const r = await api.post('/products/groups', {
        program_id: parseInt(selectedProgramId),
        ...groupForm,
        sort_order: parseInt(groupForm.sort_order) || 0,
      });
      setGroups(prev => [...prev, { ...r.data, products: [] }]);
      setExpanded(prev => new Set([...prev, r.data.id]));
      setShowAddGroup(false);
      setGroupForm({ name: '', description: '', sort_order: 0 });
    } catch { setError('Fehler beim Erstellen der Gruppe.'); }
    finally { setLoading(false); }
  };

  const handleUpdateGroup = async (e, groupId) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const r = await api.put(`/products/groups/${groupId}`, {
        ...editGroupForm,
        sort_order: parseInt(editGroupForm.sort_order) || 0,
      });
      setGroups(prev => prev.map(g => g.id === groupId ? { ...r.data, products: g.products } : g));
      setEditingGroupId(null);
    } catch { setError('Fehler beim Aktualisieren der Gruppe.'); }
    finally { setLoading(false); }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Gruppe und alle Produkte darin wirklich löschen?')) return;
    setLoading(true); setError('');
    try {
      await api.delete(`/products/groups/${groupId}`);
      setGroups(prev => prev.filter(g => g.id !== groupId));
    } catch { setError('Fehler beim Löschen der Gruppe.'); }
    finally { setLoading(false); }
  };

  // ── Product operations ────────────────────────────────────────────────────

  const handleAddProduct = async (e, groupId) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const r = await api.post('/products/items', { group_id: groupId, ...productPayload(productForm) });
      setGroups(prev => prev.map(g =>
        g.id === groupId ? { ...g, products: [...g.products, r.data] } : g
      ));
      setAddingInGroup(null);
      setProductForm(emptyProductForm);
    } catch { setError('Fehler beim Erstellen des Produkts.'); }
    finally { setLoading(false); }
  };

  const handleUpdateProduct = async (e, productId, groupId) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const r = await api.put(`/products/items/${productId}`, productPayload(editProductForm));
      setGroups(prev => prev.map(g =>
        g.id === groupId
          ? { ...g, products: g.products.map(p => p.id === productId ? r.data : p) }
          : g
      ));
      setEditingProductId(null);
    } catch { setError('Fehler beim Aktualisieren des Produkts.'); }
    finally { setLoading(false); }
  };

  const handleDeleteProduct = async (productId, groupId) => {
    if (!window.confirm('Produkt wirklich löschen?')) return;
    setLoading(true); setError('');
    try {
      await api.delete(`/products/items/${productId}`);
      setGroups(prev => prev.map(g =>
        g.id === groupId ? { ...g, products: g.products.filter(p => p.id !== productId) } : g
      ));
    } catch { setError('Fehler beim Löschen des Produkts.'); }
    finally { setLoading(false); }
  };

  const startEditGroup = (group) => {
    setEditingGroupId(group.id);
    setEditGroupForm({ name: group.name, description: group.description || '', sort_order: group.sort_order });
    setShowAddGroup(false);
  };

  const startEditProduct = (product) => {
    setEditingProductId(product.id);
    setEditProductForm({
      name: product.name,
      description: product.description || '',
      short_description: product.short_description || '',
      image_url: product.image_url || '',
      image_alt: product.image_alt || '',
      price: product.price,
      currency: product.currency,
      duration_type: product.duration_type,
      is_active: product.is_active,
      sort_order: product.sort_order,
      highlight: product.highlight,
      badge: product.badge || '',
      features: product.features || '',
      plan_key: product.plan_key || '',
      modules: product.modules || '',
      user_limit: product.user_limit ?? '',
      product_key: product.product_key || '',
      product_type: product.product_type || 'LICENSED',
      customer_limit: product.customer_limit ?? '',
    });
    setAddingInGroup(null);
  };

  const applyTemplate = (templateKey, target = 'new') => {
    const template = PRODUCT_TEMPLATES[templateKey];
    if (!template) return;

    if (target === 'edit') {
      setEditProductForm(prev => ({ ...prev, ...template }));
      return;
    }

    setProductForm(prev => ({ ...prev, ...template }));
  };

  const selectedProgram = programs.find(p => p.id === parseInt(selectedProgramId));

  const handleProgramChange = (e) => {
    const id = e.target.value;
    setSelectedProgramId(id);
    setShowAddGroup(false);
    setEditingGroupId(null);
    setDiscordRoleMsg('');
    const prog = programs.find(p => p.id === parseInt(id));
    setDiscordRoleId(prog?.discord_role_id || '');
  };

  const saveDiscordRole = async () => {
    if (!selectedProgramId) return;
    setDiscordRoleSaving(true);
    setDiscordRoleMsg('');
    try {
      await api.put(`/programs/${selectedProgramId}`, {
        name: selectedProgram.name,
        description: selectedProgram.description || null,
        download_url: selectedProgram.download_url || null,
        discord_role_id: discordRoleId || null,
      });
      setDiscordRoleMsg('✓ Gespeichert');
      setPrograms(prev => prev.map(p =>
        p.id === parseInt(selectedProgramId) ? { ...p, discord_role_id: discordRoleId || null } : p
      ));
    } catch {
      setDiscordRoleMsg('Fehler beim Speichern');
    } finally {
      setDiscordRoleSaving(false);
      setTimeout(() => setDiscordRoleMsg(''), 3000);
    }
  };

  return (
    <div>
      <div className="page-heading">
        <h2>Produkte</h2>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Program selector */}
      <div className="card" style={{ marginBottom: '24px', padding: '16px 20px' }}>
        <div className="program-selector" style={{ margin: 0, flexWrap: 'wrap', gap: '12px' }}>
          <label htmlFor="prog-select">Programm</label>
          <select
            id="prog-select"
            className="form-select"
            style={{ width: 'auto', minWidth: '220px' }}
            value={selectedProgramId}
            onChange={handleProgramChange}
          >
            <option value="">– Programm auswählen –</option>
            {programs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>

          {selectedProgramId && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto', flexWrap: 'wrap' }}>
                <label style={{ fontSize: '13px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                  Discord Role-ID
                </label>
                <input
                  type="text"
                  className="form-input"
                  style={{ width: '200px', padding: '7px 12px', fontSize: '13px' }}
                  value={discordRoleId}
                  onChange={e => setDiscordRoleId(e.target.value)}
                  placeholder="z. B. 1194391227206996051"
                />
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={saveDiscordRole}
                  disabled={discordRoleSaving}
                >
                  {discordRoleSaving ? 'Speichern…' : 'Speichern'}
                </button>
                {discordRoleMsg && (
                  <span style={{ fontSize: '12px', color: discordRoleMsg.startsWith('✓') ? 'var(--green)' : 'var(--red)' }}>
                    {discordRoleMsg}
                  </span>
                )}
              </div>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => { setShowAddGroup(true); setEditingGroupId(null); }}
                style={{ flexShrink: 0 }}
              >
                + Neue Gruppe
              </button>
            </>
          )}
        </div>
      </div>

      {!selectedProgramId && (
        <div className="empty-state">Bitte oben ein Programm auswählen.</div>
      )}

      {selectedProgramId && (
        <>
          {/* Add group form */}
          {showAddGroup && (
            <div className="card" style={{ marginBottom: '16px', borderColor: 'rgba(37,132,255,0.3)' }}>
              <p className="card-title">Neue Gruppe – {selectedProgram?.name}</p>
              <form onSubmit={handleAddGroup}>
                <GroupFormFields form={groupForm} setForm={setGroupForm} />
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={loading || !groupForm.name}>
                    {loading ? 'Speichern…' : 'Gruppe erstellen'}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowAddGroup(false)}>
                    Abbrechen
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Groups list */}
          {groups.length === 0 && !loading && (
            <div className="empty-state">Noch keine Produktgruppen vorhanden. Füge eine Gruppe hinzu.</div>
          )}
          {loading && groups.length === 0 && <div className="empty-state">Lädt…</div>}

          {groups.map(group => (
            <div key={group.id} className="group-item">
              {/* Group header */}
              {editingGroupId === group.id ? (
                <div className="group-edit-form">
                  <p className="card-title">Gruppe bearbeiten</p>
                  <form onSubmit={(e) => handleUpdateGroup(e, group.id)}>
                    <GroupFormFields form={editGroupForm} setForm={setEditGroupForm} />
                    <div className="form-actions">
                      <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? 'Speichern…' : 'Aktualisieren'}
                      </button>
                      <button type="button" className="btn btn-secondary" onClick={() => setEditingGroupId(null)}>
                        Abbrechen
                      </button>
                    </div>
                  </form>
                </div>
              ) : (
                <div className="group-item-header" onClick={() => toggleExpand(group.id)}>
                  <div className="group-item-title">
                    <span className="group-expand-icon">{expanded.has(group.id) ? '▾' : '▸'}</span>
                    <span className="group-name">{group.name}</span>
                    {!group.is_active && <span className="badge badge-inactive" style={{ marginLeft: '8px' }}>Inaktiv</span>}
                    <span className="group-count">{group.products.length} Produkt{group.products.length !== 1 ? 'e' : ''}</span>
                  </div>
                  {group.description && (
                    <span className="group-desc">{group.description}</span>
                  )}
                  <div className="group-actions" onClick={e => e.stopPropagation()}>
                    <button className="btn btn-secondary btn-sm" onClick={() => startEditGroup(group)}>
                      Bearbeiten
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDeleteGroup(group.id)}>
                      Löschen
                    </button>
                  </div>
                </div>
              )}

              {/* Group products (expanded) */}
              {expanded.has(group.id) && editingGroupId !== group.id && (
                <div className="group-item-body">
                  {group.products.length > 0 ? (
                    <div className="table-wrap" style={{ borderRadius: '0', border: 'none', borderTop: '1px solid var(--border)' }}>
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Preis</th>
                            <th>Laufzeit</th>
                            <th>Hervorgehoben</th>
                            <th>Reihenfolge</th>
                            <th>Status</th>
                            <th>Aktionen</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.products.map(product => (
                            <React.Fragment key={product.id}>
                              <tr className={!product.is_active ? 'inactive' : ''}>
                                <td style={{ fontWeight: 600 }}>
                                  {product.name}
                                  {product.badge && (
                                    <span className="badge badge-cyan" style={{ marginLeft: '8px', fontSize: '10px' }}>
                                      {product.badge}
                                    </span>
                                  )}
                                </td>
                                <td>
                                  <span style={{ fontWeight: 700, color: 'var(--blue)' }}>
                                    {parseFloat(product.price).toFixed(2).replace('.', ',')} {product.currency}
                                  </span>
                                </td>
                                <td>
                                  <span className="badge badge-cyan">
                                    {DURATION_LABELS[product.duration_type] || product.duration_type}
                                  </span>
                                </td>
                                <td>
                                  {product.highlight
                                    ? <span className="badge badge-active">Ja</span>
                                    : <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>–</span>
                                  }
                                </td>
                                <td style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{product.sort_order}</td>
                                <td>
                                  <span className={`badge ${product.is_active ? 'badge-active' : 'badge-inactive'}`}>
                                    {product.is_active ? 'Aktiv' : 'Inaktiv'}
                                  </span>
                                </td>
                                <td>
                                  <div style={{ display: 'flex', gap: '6px' }}>
                                    <button
                                      className="btn btn-secondary btn-sm"
                                      onClick={() => {
                                        startEditProduct(product);
                                        setExpanded(prev => new Set([...prev, group.id]));
                                      }}
                                    >
                                      Bearbeiten
                                    </button>
                                    <button
                                      className="btn btn-danger btn-sm"
                                      onClick={() => handleDeleteProduct(product.id, group.id)}
                                    >
                                      Löschen
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              {editingProductId === product.id && (
                                <tr>
                                  <td colSpan={7} style={{ padding: '16px', background: 'var(--surface-2)' }}>
                                    <p className="card-title">Produkt bearbeiten</p>
                                    <form onSubmit={(e) => handleUpdateProduct(e, product.id, group.id)}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                                        <label className="form-label" style={{ margin: 0 }}>Quick-Template</label>
                                        <select
                                          className="form-select"
                                          style={{ minWidth: '180px' }}
                                          defaultValue=""
                                          onChange={(e) => {
                                            if (e.target.value) applyTemplate(e.target.value, 'edit');
                                            e.target.value = '';
                                          }}
                                        >
                                          <option value="">– Vorlage wählen –</option>
                                          <option value="monthly">Monats-Template</option>
                                          <option value="yearly">Jahres-Template</option>
                                          <option value="lifetime">Lifetime-Template</option>
                                        </select>
                                      </div>
                                      <ProductFormFields form={editProductForm} setForm={setEditProductForm} />
                                      <div className="form-actions">
                                        <button type="submit" className="btn btn-primary" disabled={loading}>
                                          {loading ? 'Speichern…' : 'Aktualisieren'}
                                        </button>
                                        <button type="button" className="btn btn-secondary" onClick={() => setEditingProductId(null)}>
                                          Abbrechen
                                        </button>
                                      </div>
                                    </form>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ padding: '16px 20px', color: 'var(--text-dim)', fontSize: '13px', borderTop: '1px solid var(--border)' }}>
                      Noch keine Produkte in dieser Gruppe.
                    </div>
                  )}

                  {/* Add product form or button */}
                  {addingInGroup === group.id ? (
                    <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border)', background: 'rgba(37,132,255,0.03)' }}>
                      <p className="card-title">Neues Produkt</p>
                      <form onSubmit={(e) => handleAddProduct(e, group.id)}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <label className="form-label" style={{ margin: 0 }}>Quick-Template</label>
                            <select
                              className="form-select"
                              style={{ minWidth: '180px' }}
                              defaultValue=""
                              onChange={(e) => {
                                if (e.target.value) applyTemplate(e.target.value, 'new');
                                e.target.value = '';
                              }}
                            >
                              <option value="">– Vorlage wählen –</option>
                              <option value="monthly">Monats-Template</option>
                              <option value="yearly">Jahres-Template</option>
                              <option value="lifetime">Lifetime-Template</option>
                            </select>
                          </div>
                        </div>
                        <ProductFormFields form={productForm} setForm={setProductForm} />
                        <div className="form-actions">
                          <button type="submit" className="btn btn-primary" disabled={loading || !productForm.name || !productForm.price}>
                            {loading ? 'Speichern…' : 'Produkt hinzufügen'}
                          </button>
                          <button type="button" className="btn btn-secondary" onClick={() => { setAddingInGroup(null); setProductForm(emptyProductForm); }}>
                            Abbrechen
                          </button>
                        </div>
                      </form>
                    </div>
                  ) : (
                    <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)' }}>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => { setAddingInGroup(group.id); setEditingProductId(null); setProductForm(emptyProductForm); }}
                      >
                        + Produkt hinzufügen
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function GroupFormFields({ form, setForm }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '14px', alignItems: 'start' }}>
      <div className="form-group" style={{ margin: 0 }}>
        <label className="form-label">Name *</label>
        <input className="form-input" required value={form.name}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="z. B. Premium" />
      </div>
      <div className="form-group" style={{ margin: 0 }}>
        <label className="form-label">Beschreibung</label>
        <input className="form-input" value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional" />
      </div>
      <div className="form-group" style={{ margin: 0 }}>
        <label className="form-label">Reihenfolge</label>
        <input className="form-input" type="number" min="0" style={{ width: '80px' }} value={form.sort_order}
          onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))} />
      </div>
    </div>
  );
}

function ProductFormFields({ form, setForm }) {
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '14px', marginBottom: '14px', alignItems: 'start' }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Name *</label>
          <input className="form-input" required value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="z. B. Monatlich" />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Preis *</label>
          <input className="form-input" required type="number" step="0.01" min="0" value={form.price}
            onChange={e => setForm(f => ({ ...f, price: e.target.value }))} placeholder="2.99" />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Währung</label>
          <select className="form-select" value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}>
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
          </select>
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Laufzeit</label>
          <select className="form-select" value={form.duration_type} onChange={e => setForm(f => ({ ...f, duration_type: e.target.value }))}>
            <option value="WEEK">Woche</option>
            <option value="MONTH">Monat</option>
            <option value="YEAR">Jahr</option>
            <option value="UNLIMITED">Unbegrenzt</option>
          </select>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px', alignItems: 'start' }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Beschreibung</label>
          <input className="form-input" value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Kurze Beschreibung (optional)" />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Badge-Text</label>
          <input className="form-input" value={form.badge}
            onChange={e => setForm(f => ({ ...f, badge: e.target.value }))} placeholder="z. B. 2 Monate gratis" />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '14px', marginBottom: '14px', alignItems: 'start' }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Kurzbeschreibung für Shop</label>
          <input className="form-input" value={form.short_description}
            onChange={e => setForm(f => ({ ...f, short_description: e.target.value }))} placeholder="z. B. Für alle, die schneller starten wollen" />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Bild-URL</label>
          <input className="form-input" value={form.image_url}
            onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))} placeholder="https://.../bild.jpg" />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Alt-Text für Bild</label>
        <input className="form-input" value={form.image_alt}
          onChange={e => setForm(f => ({ ...f, image_alt: e.target.value }))} placeholder="Beschreibung des Bildes für Accessibility" />
      </div>

      {form.image_url && (
        <div className="form-group">
          <label className="form-label">Bildvorschau</label>
          <img
            src={form.image_url}
            alt={form.image_alt || form.name || 'Produktbild'}
            style={{
              width: '220px',
              maxHeight: '150px',
              objectFit: 'cover',
              borderRadius: '12px',
              border: '1px solid var(--border)',
              background: 'var(--surface-2)',
            }}
          />
        </div>
      )}

      <div className="form-group">
        <label className="form-label">Features (eine pro Zeile)</label>
        <textarea className="form-textarea" style={{ minHeight: '80px' }} value={form.features}
          onChange={e => setForm(f => ({ ...f, features: e.target.value }))}
          placeholder={'Unbegrenzte Mitglieder\nAlle Module\nSofortige Aktivierung'} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: '14px', marginBottom: '14px', alignItems: 'start' }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Plan-Key (für Lizenz-Verify)</label>
          <input className="form-input" value={form.plan_key}
            onChange={e => setForm(f => ({ ...f, plan_key: e.target.value }))} placeholder="z. B. plus" />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Module (kommagetrennt, für Lizenz-Verify)</label>
          <input className="form-input" value={form.modules}
            onChange={e => setForm(f => ({ ...f, modules: e.target.value }))} placeholder="discord, kalender, einkauf" />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Nutzerlimit (leer = unbegrenzt)</label>
          <input className="form-input" type="number" min="1" value={form.user_limit}
            onChange={e => setForm(f => ({ ...f, user_limit: e.target.value }))} placeholder="z. B. 2" />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px', marginBottom: '14px', alignItems: 'start' }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Produkt-Key (eindeutig, FiveM)</label>
          <input className="form-input" value={form.product_key}
            onChange={e => setForm(f => ({ ...f, product_key: e.target.value }))} placeholder="z. B. kc-growth" />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Produkttyp</label>
          <select className="form-select" value={form.product_type}
            onChange={e => setForm(f => ({ ...f, product_type: e.target.value }))}>
            <option value="LICENSED">Lizenz</option>
            <option value="ADDON">Add-on (erweitert Module einer Basislizenz)</option>
          </select>
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Kundenlimit (leer = unbegrenzt)</label>
          <input className="form-input" type="number" min="0" value={form.customer_limit}
            disabled={form.product_type === 'ADDON'}
            onChange={e => setForm(f => ({ ...f, customer_limit: e.target.value }))} placeholder="z. B. 50" />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: 'var(--text-muted)' }}>
          <input type="checkbox" checked={form.highlight} onChange={e => setForm(f => ({ ...f, highlight: e.target.checked }))} />
          Hervorheben (empfohlen)
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: 'var(--text-muted)' }}>
          <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
          Aktiv (im Shop sichtbar)
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--text-muted)' }}>
          <span>Reihenfolge:</span>
          <input className="form-input" type="number" min="0" style={{ width: '70px', padding: '6px 10px' }}
            value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))} />
        </div>
      </div>
    </>
  );
}

export default ProductManagement;
